// Pure helpers for the admin program screen (P11, plan §7.7). They only read the answers of
// GET /api/cms/events/{eventId}/program (heartscms program.ProgramView) and of the People area's
// person list; nothing here decides anything the backend already decided (`openSpot`, `removedAt`).
// The text pickers are shared with the public program so both views read DE/EN the same way.
import { formatTime, localDate, pick, timeRange } from '../program/programModel';

// pick/formatTime/timeRange/localDate are the public program's helpers, re-used so both views read
// DE/EN and times the same way.
export { localDate, pick, timeRange };

// Roles in the order the admin reads them (chairs first, same as the public view); the roles an
// entry actually carries are grouped in this order.
// The six states of an assignment (AssignmentStatus). Colour is never the only signal: every state
// is printed as a word next to its tag and carries its own glyph and left border, so it stays
// readable for colour-blind users. Contrast of fg on bg is >= 6:1 (WCAG AA needs 4.5:1).
export const STATUS_META = {
  PROPOSED: { id: 'programAdmin.status.PROPOSED', def: 'Not asked yet', bg: '#f1f5f9', fg: '#334155', border: '#94a3b8' },
  INVITED: { id: 'programAdmin.status.INVITED', def: 'Invited', bg: '#ede9fe', fg: '#5b21b6', border: '#7c3aed' },
  CONFIRMED: { id: 'programAdmin.status.CONFIRMED', def: 'Confirmed', bg: '#dcfce7', fg: '#166534', border: '#15803d' },
  DECLINED: { id: 'programAdmin.status.DECLINED', def: 'Declined', bg: '#fee2e2', fg: '#991b1b', border: '#b91c1c' },
  WITHDRAWN: { id: 'programAdmin.status.WITHDRAWN', def: 'Withdrawn', bg: '#ffedd5', fg: '#9a3412', border: '#c2410c' },
  CANCELLED: { id: 'programAdmin.status.CANCELLED', def: 'Cancelled', bg: '#e2e8f0', fg: '#475569', border: '#64748b' },
};

export const statusMeta = (status) => STATUS_META[status] || STATUS_META.PROPOSED;

export const roleLabelId = (role) => `programAdmin.role.${role}`;

// The state of the spot as the admin reads it: an open spot is what needs filling, whichever of the
// two reasons applies (nobody on it, or the person is gone).
export const isOpenSpot = (a) => (a.openSpot != null
  ? !!a.openSpot
  : a.removedAt == null && (a.personId == null || ['DECLINED', 'WITHDRAWN', 'CANCELLED'].includes(a.status)));

// A declined spot whose invitation carried a suggested replacement (plan §7.6: the suggestion lives
// on the invitation that answered, so a re-invite does not lose it).
export const declineSuggestionOf = (a) => {
  const declined = historyOf(a).filter((h) => h.declineReplacementName || h.declineReplacementEmail).pop();
  if (!declined) return null;
  return { name: declined.declineReplacementName, email: declined.declineReplacementEmail, askedAt: declined.closedAt };
};

// The suggestion that is still waiting for the admin: the spot is DECLINED, not removed, and the
// invitation that closed it (the last one, as SuggestedPersonService reads it) carries the name or
// the email. A re-invited, re-filled or removed spot has no such suggestion any more.
export const pendingSuggestionOf = (a) => {
  if (a.removedAt || a.status !== 'DECLINED') return null;
  const last = historyOf(a).pop();
  if (!last || !(last.declineReplacementName || last.declineReplacementEmail)) return null;
  return declineSuggestionOf(a);
};

// The person the suggestion points at by email, if they are already among this event's people: the
// side panel names them on its button. The real matching (email, name, similar names, among all
// persons) is the backend's plan, which the confirm dialog shows (SuggestedPersonService).
export const suggestedPersonOf = (a, people) => {
  const suggestion = declineSuggestionOf(a);
  if (!suggestion?.email) return null;
  const email = suggestion.email.toLowerCase();
  return (people || []).find((p) => (p.contactEmail || '').toLowerCase() === email) || null;
};

// Every invitation of the spot, oldest first (AssignmentHistory.Asked); what the public shape calls
// history, the admin calls the trail of who was asked.
export const historyOf = (a) => (a.history || []).slice().sort((x, y) => String(x.askedAt).localeCompare(String(y.askedAt)));
export const openInvitationOf = (a) => historyOf(a).find((h) => !h.closedAt) || null;

// What the admin has to act on (owner decision): an open spot (nobody on it, or the person is
// DECLINED, WITHDRAWN or CANCELLED), and an INVITED spot whose person has reached the reminder
// maximum without answering (the person row's reminders.remindersSent >= reminders.maxReminders -
// the backend's own count, reported, never derived here). The old "invited for over a week" rule is
// gone: a reminder is per person and event, so the row is where the count lives.
const needsAttention = (a, people) => {
  if (a.removedAt) return false;
  if (isOpenSpot(a)) return true;
  if (a.status !== 'INVITED') return false;
  const reminders = personReminders(personById(people, a.personId));
  return reminders.maxReminders > 0 && (reminders.remindersSent || 0) >= reminders.maxReminders;
};

export const isEntry = (node) => !!node && node.type === 'ENTRY';
export const isElement = (node) => !!node && node.type === 'DISPLAY_ELEMENT';

const bySort = (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0);

// ---------------------------------------------------------------- what the backend allows

// The state machine of one spot (AssignmentTransitions), as the screen needs it: which action the
// admin may take in which state. `neverAsked` is the fixture/API fact behind the move rule (the
// invitations of a spot are its history; nobody asked means no history row).
//
//   invite   PROPOSED, DECLINED, WITHDRAWN or CANCELLED, with a person (re-invite is allowed; an
//            INVITED spot is reminded, a CONFIRMED one never invited, an open spot never invited)
//   remind   INVITED (the reminder itself is per person and event, see reminderStateOf)
//   uninvite INVITED -> CANCELLED
//   decline  INVITED (the admin records what the person said, D18)
//   withdraw CONFIRMED -> WITHDRAWN
//   replace  everything but CONFIRMED (from INVITED the open invitation is cancelled first)
//   remove   everything but CONFIRMED (an INVITED one is uninvited first)
//   move     PROPOSED and nobody was ever asked about it
// A removed spot has no action at all, and neither has a spot of a removed entry.
export const wasAsked = (a) => (a.history || []).length > 0;

// The states an invitation (or a re-invitation, D10) may start from.
export const INVITE_FROM = ['PROPOSED', 'DECLINED', 'WITHDRAWN', 'CANCELLED'];

export const canInvite = (a) => !a.removedAt && a.personId != null && INVITE_FROM.includes(a.status);
export const canRemind = (a) => !a.removedAt && a.personId != null && a.status === 'INVITED';
export const canUninvite = (a) => !a.removedAt && a.status === 'INVITED';
export const canRecordDecline = (a) => !a.removedAt && a.status === 'INVITED';
export const canWithdraw = (a) => !a.removedAt && a.status === 'CONFIRMED';
export const canReplace = (a) => !a.removedAt && a.status !== 'CONFIRMED';
export const canRemoveSpot = (a) => !a.removedAt && a.status !== 'CONFIRMED';
export const canMoveSpot = (a) => !a.removedAt && a.status === 'PROPOSED' && !wasAsked(a);

// The actions of one spot, for the row and the panel. `remindersStoppedByPerson` is not part of it:
// a reminder is per person, the panel asks reminderStateFor for that.
export const spotCapabilities = (a, entryRemoved = false) => {
  const blocked = !!a.removedAt || !!entryRemoved;
  return {
    removed: blocked,
    invite: !blocked && canInvite(a),
    remind: !blocked && canRemind(a),
    uninvite: !blocked && canUninvite(a),
    decline: !blocked && canRecordDecline(a),
    withdraw: !blocked && canWithdraw(a),
    replace: !blocked && canReplace(a),
    remove: !blocked && canRemoveSpot(a),
    move: !blocked && canMoveSpot(a),
  };
};

// ---------------------------------------------------------------- the subtree of an entry

// Every spot at or below a node, with the entry it sits on - what the entry rules look at (plan §7.6:
// an entry's removal owns its whole subtree, not only its own spots).
export const subtreeSpots = (node) => {
  const out = [];
  const walk = (entry) => {
    (entry.assignments || []).forEach((a) => out.push({ assignment: a, entry }));
    (entry.children || []).forEach((child) => { if (isEntry(child)) walk(child); });
  };
  if (isEntry(node)) walk(node);
  return out;
};

// The first spot that forbids removing this entry: a CONFIRMED one anywhere in it (not removed).
export const confirmedInSubtree = (node) => subtreeSpots(node)
  .find(({ assignment }) => !assignment.removedAt && assignment.status === 'CONFIRMED') || null;

// The spots of the subtree whose open invitation a removal would cancel (one email per person).
export const invitedInSubtree = (node) => subtreeSpots(node)
  .filter(({ assignment }) => !assignment.removedAt && assignment.status === 'INVITED');

// Nobody was ever asked in the whole subtree: the removal deletes instead of removing (plan §7.6).
export const askedInSubtree = (node) => subtreeSpots(node).some(({ assignment }) => wasAsked(assignment));

export const canRemoveEntry = (entry) => !entry.removedAt && !confirmedInSubtree(entry);

// The entries a node can be swapped with, in the order they are printed: its parent's entry children,
// or - at the top level - the entries of its own day. The first has nothing above it, the last
// nothing below it (the "up"/"down" buttons).
export const siblingEntries = (program, entry, parent = null) => {
  if (entry.depth === 0) {
    return (program?.entries || [])
      .filter((n) => isEntry(n) && n.eventDayId === entry.eventDayId && !n.removedAt)
      .slice().sort(bySort);
  }
  return (parent?.children || []).filter((n) => isEntry(n) && !n.removedAt).slice().sort(bySort);
};

export const positionOf = (siblings, entry) => {
  const index = siblings.findIndex((e) => e.id === entry.id);
  return { index, count: siblings.length, first: index === 0, last: index === siblings.length - 1 };
};

const assignmentsOf = (entry) => (entry.assignments || []).filter((a) => !a.removedAt).slice().sort(bySort);
export const removedAssignmentsOf = (entry) => (entry.assignments || []).filter((a) => a.removedAt).slice().sort(bySort);
export const childrenOf = (node) => (node?.children || []);

// The live spots of one entry, optionally only those that need attention (the filter of §7.7 item 4).
export const shownAssignmentsOf = (entry, needsAttentionOnly, people) => assignmentsOf(entry)
  .filter((a) => !needsAttentionOnly || needsAttention(a, people));

// Whether anything at or below this node needs attention - what keeps the filter from emptying a
// block that still holds a spot to fill two levels down.
export const subtreeNeedsAttention = (node, people) => {
  if (!isEntry(node)) return false;
  if ((node.assignments || []).some((a) => needsAttention(a, people))) return true;
  return childrenOf(node).some((child) => subtreeNeedsAttention(child, people));
};

export const countNeedingAttention = (program, people) => {
  let count = 0;
  walkEntries(program, (entry) => { (entry.assignments || []).forEach((a) => { if (needsAttention(a, people)) count += 1; }); });
  return count;
};

// Every assignment of the tree, in reading order, with its entry - the flat list the counts, the
// People area and the selection index are built from.
const walkEntries = (program, visit, parent = null, depth = 0) => {
  (program?.entries || []).filter(isEntry).forEach((entry) => {
    visit(entry, parent, depth);
    const walk = (children, parentEntry, childDepth) => (children || []).forEach((n) => {
      if (!isEntry(n)) return;
      visit(n, parentEntry, childDepth);
      walk(n.children, n, childDepth + 1);
    });
    walk(entry.children, entry, depth + 1);
  });
};

export const indexProgram = (program) => {
  const entries = new Map();
  const spots = new Map();
  walkEntries(program, (entry, parent, depth) => {
    entries.set(entry.id, { entry, parent, depth });
    (entry.assignments || []).forEach((a) => spots.set(a.id, { assignment: a, entry }));
  });
  return { entries, spots };
};

export const spotCount = (program) => {
  let count = 0;
  walkEntries(program, (entry) => { count += (entry.assignments || []).length; });
  return count;
};

// ---------------------------------------------------------------- the day's timeline

const nodesOfDay = (program, dayId) => (program?.entries || [])
  .filter((n) => n.eventDayId === dayId)
  .slice()
  .sort(bySort);

// The same layout the public timeline uses (a time column, parallel blocks in one slot, untimed
// blocks per room). Removed nodes are returned separately so the screen can put them behind the
// "show removed" switch at the end of their day instead of in the middle of the timetable.
export const layoutDay = (program, dayId) => {
  const all = nodesOfDay(program, dayId);
  const live = all.filter((n) => n.removedAt == null);
  const elements = live.filter(isElement);
  const entries = live.filter(isEntry);

  // A top-level display element sits right after the entry that precedes it in the day's shared
  // sort_order sequence (design §4.4.1); the one without a predecessor opens the day.
  const after = new Map();
  const before = [];
  elements.forEach((el) => {
    let anchor = null;
    entries.forEach((e) => {
      if (e.sortOrder < el.sortOrder && (anchor == null || e.sortOrder > anchor.sortOrder)) anchor = e;
    });
    if (!anchor) before.push(el);
    else {
      if (!after.has(anchor.id)) after.set(anchor.id, []);
      after.get(anchor.id).push(el);
    }
  });

  const slotMap = new Map();
  entries.filter((e) => e.startTime).forEach((e) => {
    const key = formatTime(e.startTime);
    if (!slotMap.has(key)) slotMap.set(key, []);
    slotMap.get(key).push(e);
  });
  const slots = [...slotMap.entries()]
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([time, list]) => ({ time, entries: list, after: list.flatMap((e) => after.get(e.id) || []) }));

  const roomRank = new Map((program?.rooms || []).map((r, i) => [r.id, i]));
  const untimedMap = new Map();
  entries.filter((e) => !e.startTime).forEach((e) => {
    const key = e.roomId ?? null;
    if (!untimedMap.has(key)) untimedMap.set(key, []);
    untimedMap.get(key).push({ entry: e, after: after.get(e.id) || [] });
  });
  const untimed = [...untimedMap.entries()]
    .map(([roomId, items]) => ({ roomId, items }))
    .sort((a, b) => (a.roomId == null ? 1 : b.roomId == null ? -1 : (roomRank.get(a.roomId) ?? 0) - (roomRank.get(b.roomId) ?? 0)));

  const removed = all.filter((n) => n.removedAt != null).sort(bySort);

  return { before, slots, untimed, removed };
};

// The days that hold something, in the backend's order (ProgramView.days).
export const daysOf = (program) => (program?.days || []).filter(
  (day) => nodesOfDay(program, day.id).length > 0,
);

export const dayLabel = (intl, language, day) => {
  const label = pick(language, day.labelDe, day.labelEn);
  if (label) return label;
  const date = localDate(day.day);
  return date ? intl.formatDate(date, { weekday: 'long', day: 'numeric', month: 'long' }) : '';
};

// The room of a top-level entry, and whether it sits in another venue than the event's default one.
export const placeOf = (program, roomId) => {
  const room = (program?.rooms || []).find((r) => r.id === roomId) || null;
  const venue = room ? (program?.venues || []).find((v) => v.id === room.venueId) || null : null;
  const otherVenue = venue && program?.event?.defaultVenueId != null && venue.id !== program.event.defaultVenueId ? venue : null;
  return { room, venue: otherVenue };
};

export const roomName = (language, room) => (room ? pick(language, room.nameDe, room.nameEn) : '');
export const venueText = (venue) => (venue ? [venue.name, venue.city].filter(Boolean).join(', ') : '');

export const nodeTitle = (node, language) => pick(language, node.titleDe, node.titleEn);
export const nodeLabel = (node, language) => pick(language, node.labelDe, node.labelEn);

// The one-line address of an entry for the panels, the People table and the "move to" picker.
export const entryPath = (program, entry, language) => {
  const day = (program?.days || []).find((d) => d.id === entry.eventDayId);
  const parts = [];
  if (day) parts.push(pick(language, day.labelDe, day.labelEn) || day.day);
  if (entry.startTime) parts.push(timeRange(entry.startTime, entry.endTime));
  const { room } = placeOf(program, entry.roomId);
  if (room) parts.push(roomName(language, room));
  return parts.join(' · ');
};

// What is printed where the name would be while no name is shown (ProgramView.placeholderLabel
// defaults to "TBA").
export const spotLabel = (a) => a.personName || a.placeholderLabel || null;

// ---------------------------------------------------------------- the People area

// A date-only value ("2026-11-05") is read as the local day, never as UTC midnight.
const asDate = (value) => (/^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? localDate(value) : new Date(value));

export const formatDay = (intl, value) => (value
  ? intl.formatDate(asDate(value), { day: '2-digit', month: '2-digit', year: 'numeric' })
  : null);

export const formatDateTime = (intl, value) => (value
  ? intl.formatDate(asDate(value), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : null);

// One row per person at this event: who they are (EventPeopleService.Row) plus every spot they hold,
// read from the program (live ones only - a removed spot is not part of the event any more).
export const peopleRows = (program, people) => {
  const byPerson = new Map();
  walkEntries(program, (entry) => {
    (entry.assignments || []).forEach((a) => {
      if (a.removedAt || a.personId == null) return;
      if (!byPerson.has(a.personId)) byPerson.set(a.personId, []);
      byPerson.get(a.personId).push({
        assignmentId: a.id, entryId: entry.id, entry, role: a.role, status: a.status, openSpot: isOpenSpot(a),
      });
    });
  });
  return (people || []).map((person) => ({
    ...person,
    spots: (byPerson.get(person.personId) || []).sort((x, y) => (x.entry.sortOrder ?? 0) - (y.entry.sortOrder ?? 0)),
  }));
};

// ---------------------------------------------------------------- one person's reminders

// Reminders are per person and event (ReminderService), not per spot: one email lists every open,
// non-stopped invitation of that person at the event. These read the reminder state the API returns
// with the person (EventPeopleService.Row.reminders) - reported, never derived twice.
export const personById = (people, personId) => (people || []).find((p) => p.personId === personId) || null;

export const personReminders = (person) => (person?.reminders || {
  openInvitations: 0, stoppedInvitations: 0, remindersStopped: false, remindersSent: null, maxReminders: 0,
});

// Nothing open to remind: everything is answered, or every open invitation has its reminders stopped
// (ReminderService: without an anchor there is nothing to remind, the backend answers 409).
export const canRemindPerson = (person) => {
  const r = personReminders(person);
  return r.openInvitations > 0 && !r.remindersStopped;
};
export const canStopReminders = (person) => {
  const r = personReminders(person);
  return r.openInvitations > 0 && !r.remindersStopped;
};
export const canResumeReminders = (person) => !!person && personReminders(person).remindersStopped;

// An invitation needs at least one spot the backend would accept (InvitationController: an open spot,
// a CONFIRMED or a removed assignment is refused). It needs a contact address too, but a person without
// one can still be invited from here: the invite dialog asks for the address first.
export const canInvitePerson = (row) => (row?.spots || []).some((spot) => INVITE_FROM.includes(spot.status));

// The open invitations of that person at this event, counted in the program itself - what the
// reminder dialog says ("reminds X about all their open invitations").
export const openInvitationsOf = (program, personId) => {
  let count = 0;
  walkEntries(program, (entry) => {
    (entry.assignments || []).forEach((a) => {
      if (!a.removedAt && a.personId === personId && a.status === 'INVITED') count += 1;
    });
  });
  return count;
};

